import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import interviewJourneysService from "../services/interviewJourneysService";
import onboardingService from "../services/onboardingService";
import {
  InterviewJourneyPlan,
  InterviewJourneyPlanResponse,
  InterviewJourneyEmail,
} from "../types/interviewJourney";

type Step = "input" | "review" | "progress";

const InterviewJourneyGenerator: React.FC = () => {
  const navigate = useNavigate();

  // Step 1: Input
  const [interviewText, setInterviewText] = useState("");
  const [blueprints, setBlueprints] = useState<any[]>([]);
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<number | null>(
    null
  );
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState("");

  // Step 2: Review
  const [plan, setPlan] = useState<InterviewJourneyPlanResponse | null>(null);

  // Step 3: Progress
  const [currentStep, setCurrentStep] = useState<Step>("input");
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [journeyIds, setJourneyIds] = useState<number[]>([]);
  const [totalEmails, setTotalEmails] = useState(0);
  const [generateError, setGenerateError] = useState("");
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadBlueprints();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const loadBlueprints = async () => {
    try {
      const data = await onboardingService.listOnboardingBlueprints();
      setBlueprints(data);
      if (data.length > 0) setSelectedBlueprintId(data[0].id);
    } catch (err) {
      console.error("Failed to load blueprints:", err);
    }
  };

  const handleGeneratePlan = async () => {
    if (!interviewText.trim() || !selectedBlueprintId) return;
    setPlanLoading(true);
    setPlanError("");
    try {
      const result = await interviewJourneysService.generatePlan(
        interviewText,
        selectedBlueprintId
      );
      setPlan(result);
      setCurrentStep("review");
    } catch (err: any) {
      setPlanError(
        err.response?.data?.error || "Failed to generate plan. Please try again."
      );
    } finally {
      setPlanLoading(false);
    }
  };

  const handleCreateJourneys = async () => {
    if (!plan || !selectedBlueprintId) return;
    setCurrentStep("progress");
    setGenerateError("");
    setProgress(0);
    setProgressMessage("Starting journey generation...");

    try {
      const result = await interviewJourneysService.generate(
        interviewText,
        selectedBlueprintId,
        plan
      );
      setJobId(result.job_id);
      startPolling(result.job_id);
    } catch (err: any) {
      setGenerateError(
        err.response?.data?.error || "Failed to start generation."
      );
    }
  };

  const startPolling = (id: string) => {
    pollingRef.current = setInterval(async () => {
      try {
        const status = await interviewJourneysService.getStatus(id);
        setProgress(status.progress);
        if (status.progress_message)
          setProgressMessage(status.progress_message);

        if (status.status === "completed") {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setJourneyIds(status.journey_ids || []);
          setTotalEmails(status.total_emails_created || 0);
          setProgressMessage("All journeys created successfully!");
        } else if (status.status === "failed") {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setGenerateError(
            status.error_message || "Journey generation failed."
          );
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 3000);
  };

  // Plan editing helpers
  const updateJourneyName = (journeyIdx: number, name: string) => {
    if (!plan) return;
    const updated = { ...plan };
    updated.journeys = [...updated.journeys];
    updated.journeys[journeyIdx] = {
      ...updated.journeys[journeyIdx],
      name,
    };
    setPlan(updated);
  };

  const updateEmail = (
    journeyIdx: number,
    emailIdx: number,
    field: keyof InterviewJourneyEmail,
    value: string | number
  ) => {
    if (!plan) return;
    const updated = { ...plan };
    updated.journeys = [...updated.journeys];
    updated.journeys[journeyIdx] = {
      ...updated.journeys[journeyIdx],
      emails: [...updated.journeys[journeyIdx].emails],
    };
    updated.journeys[journeyIdx].emails[emailIdx] = {
      ...updated.journeys[journeyIdx].emails[emailIdx],
      [field]: value,
    };
    setPlan(updated);
  };

  const removeEmail = (journeyIdx: number, emailIdx: number) => {
    if (!plan) return;
    const updated = { ...plan };
    updated.journeys = [...updated.journeys];
    updated.journeys[journeyIdx] = {
      ...updated.journeys[journeyIdx],
      emails: updated.journeys[journeyIdx].emails.filter(
        (_, i) => i !== emailIdx
      ),
    };
    setPlan(updated);
  };

  const removeJourney = (journeyIdx: number) => {
    if (!plan) return;
    setPlan({
      ...plan,
      journeys: plan.journeys.filter((_, i) => i !== journeyIdx),
    });
  };

  // --- Rendering ---

  const renderInput = () => (
    <div>
      <h2 style={{ marginBottom: "16px" }}>Generate Journeys from Interview</h2>
      <p style={{ color: "#6b7280", marginBottom: "24px" }}>
        Paste your interview transcript below and select a blueprint. We'll
        analyze the content and propose email journey structures.
      </p>

      <div style={{ marginBottom: "16px" }}>
        <label
          style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}
        >
          Blueprint
        </label>
        <select
          value={selectedBlueprintId || ""}
          onChange={(e) => setSelectedBlueprintId(Number(e.target.value))}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          {blueprints.map((bp) => (
            <option key={bp.id} value={bp.id}>
              {bp.name || bp.newsletter_name || `Blueprint ${bp.id}`}
              {bp.reference_edition_title ? ` — ${bp.reference_edition_title}` : ""}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: "16px" }}>
        <label
          style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}
        >
          Interview Transcript
        </label>
        <textarea
          value={interviewText}
          onChange={(e) => setInterviewText(e.target.value)}
          placeholder="Paste your interview transcript here (10-15k words recommended)..."
          style={{
            width: "100%",
            minHeight: "300px",
            padding: "12px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "14px",
            fontFamily: "inherit",
            resize: "vertical",
            boxSizing: "border-box",
          }}
        />
        <div
          style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px" }}
        >
          {interviewText.split(/\s+/).filter(Boolean).length} words
        </div>
      </div>

      {planError && (
        <div
          style={{
            color: "#dc2626",
            backgroundColor: "#fef2f2",
            padding: "12px",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          {planError}
        </div>
      )}

      <button
        onClick={handleGeneratePlan}
        disabled={planLoading || !interviewText.trim() || !selectedBlueprintId}
        style={{
          padding: "10px 24px",
          backgroundColor: "#000",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor:
            planLoading || !interviewText.trim() ? "not-allowed" : "pointer",
          opacity:
            planLoading || !interviewText.trim() || !selectedBlueprintId
              ? 0.5
              : 1,
          fontSize: "14px",
        }}
      >
        {planLoading ? "Analyzing interview..." : "Generate Plan"}
      </button>
    </div>
  );

  const renderReview = () => {
    if (!plan) return null;
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h2 style={{ margin: 0 }}>Review Journey Plan</h2>
          <button
            onClick={() => setCurrentStep("input")}
            style={{
              padding: "6px 14px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              backgroundColor: "#fff",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Back to Input
          </button>
        </div>
        <p style={{ color: "#6b7280", marginBottom: "24px" }}>
          Review and edit the proposed journeys below. You can modify names,
          titles, delays, or remove emails/journeys.
        </p>

        {plan.journeys.map((journey, jIdx) => (
          <div
            key={jIdx}
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "16px",
              backgroundColor: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "12px",
              }}
            >
              <div style={{ flex: 1, marginRight: "12px" }}>
                <input
                  type="text"
                  value={journey.name}
                  onChange={(e) => updateJourneyName(jIdx, e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "16px",
                    fontWeight: 600,
                    boxSizing: "border-box",
                  }}
                />
                <div
                  style={{
                    color: "#6b7280",
                    fontSize: "13px",
                    marginTop: "4px",
                  }}
                >
                  {journey.theme}
                </div>
              </div>
              {plan.journeys.length > 1 && (
                <button
                  onClick={() => removeJourney(jIdx)}
                  style={{
                    padding: "4px 10px",
                    border: "1px solid #fca5a5",
                    borderRadius: "4px",
                    backgroundColor: "#fef2f2",
                    color: "#dc2626",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Remove Journey
                </button>
              )}
            </div>

            {journey.emails.map((email, eIdx) => (
              <div
                key={eIdx}
                style={{
                  padding: "12px",
                  backgroundColor: "#f9fafb",
                  borderRadius: "6px",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#9ca3af",
                    }}
                  >
                    Email {eIdx + 1}
                  </span>
                  {journey.emails.length > 1 && (
                    <button
                      onClick={() => removeEmail(jIdx, eIdx)}
                      style={{
                        padding: "2px 8px",
                        border: "none",
                        backgroundColor: "transparent",
                        color: "#dc2626",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  value={email.title}
                  onChange={(e) =>
                    updateEmail(jIdx, eIdx, "title", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "6px 10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                    marginBottom: "6px",
                    boxSizing: "border-box",
                  }}
                />

                <textarea
                  value={email.description}
                  onChange={(e) =>
                    updateEmail(jIdx, eIdx, "description", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "6px 10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "13px",
                    minHeight: "50px",
                    resize: "vertical",
                    marginBottom: "6px",
                    boxSizing: "border-box",
                  }}
                />

                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <label style={{ fontSize: "13px", color: "#6b7280" }}>
                    Delay:
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={email.delay_amount}
                    onChange={(e) =>
                      updateEmail(
                        jIdx,
                        eIdx,
                        "delay_amount",
                        Math.max(0, parseInt(e.target.value) || 0)
                      )
                    }
                    style={{
                      width: "70px",
                      padding: "4px 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "13px",
                    }}
                  />
                  <select
                    value={email.delay_unit}
                    onChange={(e) =>
                      updateEmail(jIdx, eIdx, "delay_unit", e.target.value)
                    }
                    style={{
                      padding: "4px 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "13px",
                    }}
                  >
                    <option value="days">days</option>
                    <option value="hours">hours</option>
                    <option value="minutes">minutes</option>
                  </select>
                  {eIdx === 0 && email.delay_amount === 0 && (
                    <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                      (sent immediately)
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}

        <button
          onClick={handleCreateJourneys}
          disabled={!plan.journeys.length}
          style={{
            padding: "10px 24px",
            backgroundColor: "#000",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: plan.journeys.length ? "pointer" : "not-allowed",
            fontSize: "14px",
            opacity: plan.journeys.length ? 1 : 0.5,
          }}
        >
          Create Journeys (
          {plan.journeys.reduce((sum, j) => sum + j.emails.length, 0)} emails
          across {plan.journeys.length} journey
          {plan.journeys.length !== 1 ? "s" : ""})
        </button>
      </div>
    );
  };

  const renderProgress = () => {
    const isComplete = journeyIds.length > 0;
    const isFailed = !!generateError;

    return (
      <div>
        <h2 style={{ marginBottom: "16px" }}>
          {isComplete
            ? "Journeys Created!"
            : isFailed
              ? "Generation Failed"
              : "Generating Journeys..."}
        </h2>

        {!isFailed && (
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                width: "100%",
                height: "8px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                overflow: "hidden",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  width: `${isComplete ? 100 : progress}%`,
                  height: "100%",
                  backgroundColor: isComplete ? "#22c55e" : "#000",
                  borderRadius: "4px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <div style={{ color: "#6b7280", fontSize: "13px" }}>
              {progressMessage}
            </div>
          </div>
        )}

        {generateError && (
          <div
            style={{
              color: "#dc2626",
              backgroundColor: "#fef2f2",
              padding: "12px",
              borderRadius: "4px",
              marginBottom: "16px",
            }}
          >
            {generateError}
          </div>
        )}

        {isComplete && (
          <div>
            <p style={{ color: "#6b7280", marginBottom: "16px" }}>
              Successfully created {journeyIds.length} journey
              {journeyIds.length !== 1 ? "s" : ""} with {totalEmails} total
              emails.
            </p>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {journeyIds.map((id) => (
                <button
                  key={id}
                  onClick={() => navigate(`/journeys/${id}`)}
                  style={{
                    padding: "10px 16px",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: "14px",
                  }}
                >
                  View Journey #{id} &rarr;
                </button>
              ))}
            </div>
            <button
              onClick={() => navigate("/journeys")}
              style={{
                marginTop: "16px",
                padding: "8px 16px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                fontSize: "13px",
              }}
            >
              Back to Journeys
            </button>
          </div>
        )}

        {isFailed && (
          <button
            onClick={() => {
              setGenerateError("");
              setCurrentStep("review");
            }}
            style={{
              padding: "8px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              backgroundColor: "#fff",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Back to Review
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: "20px", maxWidth: "800px" }}>
      {currentStep === "input" && renderInput()}
      {currentStep === "review" && renderReview()}
      {currentStep === "progress" && renderProgress()}
    </div>
  );
};

export default InterviewJourneyGenerator;
